import { batch } from 'react-redux';
import ClientStore from '../redux/store';
import ServerStore from '../redux/server-store';
import { fetchFullDatasetwithfilters, fetchOpenSearchDataset } from '../redux/datasets/datasets.actions';
import { fetchQueryBuilderSteps } from '../redux/querybuilder/query-builder.actions';
import { fetchInitialSolutions, fetchFullSolution } from '../redux/solutions/solutions.actions';
import { fetchFullBlueprint, fetchInitialBlueprints } from '../redux/blueprints/blueprints.actions';
import { fetchFullQueryLibrary } from '../redux/querylibrary/querylibrary.actions';
import { fetchResearchLinks, fetchFAQs, fetchPageTexts, updateUTMParameters, fetchIdVariants, fetchTileNamesGt25Chars, fetchGenAIPrompts, fetchMarketplaceTooltips } from '../redux/shared/shared.actions';
import { fetchFilters, fetchOtherLanguageFilters } from '../redux/filters/filters.actions';
import extractId from './extract-id';
import SPGVariables from './spg-variables';
import { fetchAnnouncementsSummary, fetchAnnouncementsArchive } from '../redux/announcements/announcements.actions';
import {
    hasAnnouncementsSummarySessionLock,
    loadAnnouncementsArchiveCache,
    loadAnnouncementsSummaryCache,
    lockAnnouncementsSummarySession,
    wasAnnouncementsSummaryFetchedInAnotherTab
} from '../redux/announcements/announcements.storage';
import { API_ANNOUNCEMENTS_ARCHIVE, API_FETCH_ANNOUNCEMENTS_SUMMARY } from '../redux/actionTypes';

const spgVariables = SPGVariables();

let Store = ClientStore;
const GUEST_ANNOUNCEMENTS_CACHE_SCOPE = 'shared';

export const shouldFetchAnnouncementsSummary = (isLoggedIn: boolean, hasRefreshToken: boolean) => {
    return !isLoggedIn && !hasRefreshToken;
};

const fetchAnnouncementsSummaryOncePerSession = async () => {

    if (hasAnnouncementsSummarySessionLock()) {
        // Session lock exists, so try cache hydration first for guest users.
        const cachedSummary = loadAnnouncementsSummaryCache(GUEST_ANNOUNCEMENTS_CACHE_SCOPE);
        const cachedArchive = loadAnnouncementsArchiveCache(GUEST_ANNOUNCEMENTS_CACHE_SCOPE);

        if (cachedSummary) {
            Store.dispatch({
                response: cachedSummary,
                type: API_FETCH_ANNOUNCEMENTS_SUMMARY,
                isLoggedIn: false
            });
        }

        if (cachedArchive) {
            Store.dispatch({
                announcementsArchive: cachedArchive,
                type: API_ANNOUNCEMENTS_ARCHIVE
            });
        }

        // If either cache is missing, fetch again so panel does not go empty after language reload.
        if (!cachedSummary || !cachedArchive) {
            Store.dispatch(fetchAnnouncementsSummary());
            Store.dispatch(fetchAnnouncementsArchive());
        }

        return;
    }

    const fetchedInAnotherTab = await wasAnnouncementsSummaryFetchedInAnotherTab();
    const announcements = ((Store.getState().announcements || {}).Announcements || []) as Announcement[];
    const hasAnnouncementsInStore = Array.isArray(announcements) && announcements.length > 0;

    if (!fetchedInAnotherTab || !hasAnnouncementsInStore) {
        Store.dispatch(fetchAnnouncementsSummary());
        Store.dispatch(fetchAnnouncementsArchive());
    }

    lockAnnouncementsSummarySession();
};

const loadAll = () => {
    const state = Store.getState();
    const isLoggedIn = state.user && state.user.isLoggedIn;
    const hasRefreshToken = !!spgVariables.REFRESHTOKEN;

    batch(() => {
        Store.dispatch(fetchResearchLinks());
        // load all datasets and bundles from the openSearch API
        Store.dispatch(fetchOpenSearchDataset());
        // load all filters
        Store.dispatch(fetchFilters());
        // load all page texts / page sections
        Store.dispatch(fetchPageTexts());

        Store.dispatch(fetchOtherLanguageFilters());
        // load IdVariants
        Store.dispatch(fetchIdVariants());
        // load tileNamesGt25Chars
        Store.dispatch(fetchTileNamesGt25Chars());
        Store.dispatch(fetchGenAIPrompts());
        Store.dispatch(fetchMarketplaceTooltips());

        if (shouldFetchAnnouncementsSummary(isLoggedIn, hasRefreshToken)) {
            fetchAnnouncementsSummaryOncePerSession();
        }
    });
};

const start = (path: string) => {
    if (spgVariables.IS_CRAWLER_REQUEST || spgVariables.IS_SERVER_REQUEST) {
        Store = spgVariables.IS_SERVER_REQUEST ? ServerStore() : Store;
        const pathLowerCase = path.toLowerCase();
        const id = extractId(pathLowerCase);
        if (id) {
            if (pathLowerCase.indexOf('/solutions/') > -1) {
                Store.dispatch(fetchFullSolution({ id }));
            } else if (pathLowerCase.indexOf('/blueprints/') > -1) {
                Store.dispatch(fetchFullBlueprint({ id }));
            } else if (pathLowerCase.indexOf('/query-library/') > -1) {
                Store.dispatch(fetchFullQueryLibrary({ id }));
            } else {
                const isBundle = pathLowerCase.indexOf('bundle') > -1;
                Store.dispatch(fetchFullDatasetwithfilters({ id, isBundle }));
            }
        } else if (pathLowerCase.indexOf('faq') > -1) {
            Store.dispatch(fetchFAQs());
        } else {
            loadAll();
        }
    } else {
        loadAll();
        // store utm params for marketo form submission
        Store.dispatch(updateUTMParameters());
    }
};

export default start;
